import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import BotProtection from "@/components/BotProtection";
import ConsentAgreement from "@/components/ConsentAgreement";
import PhoneConsentSlide from "@/components/PhoneConsentSlide";
import { formatApiError } from "@/utils/apiError";
import { isFilled, isEmailFilled } from "@/utils/formReady";
import { trackLead } from "@/utils/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactSection() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [consent, setConsent] = useState(false);
  const phoneProvided = isFilled(form.phone);
  const canSubmit =
    isFilled(form.name) &&
    isEmailFilled(form.email) &&
    isFilled(form.message) &&
    (!phoneProvided || consent);

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.location'),
      lines: ["5401 W Cermak Road", "Cicero, IL 60804"]
    },
    {
      icon: Phone,
      title: t('contact.phone'),
      lines: ["(708) 222-6100", t('contact.hoursMonSat')]
    },
    {
      icon: Mail,
      title: t('contact.email'),
      lines: ["sales@drivenow.com", t('contact.respondWithin24hrs')]
    },
    {
      icon: Clock,
      title: t('contact.hours'),
      lines: [t('contact.hoursMonSat'), t('contact.hoursSunClosed')]
    }
  ];

  const handlePhoneChange = (value) => {
    setForm((f) => ({ ...f, phone: value }));
    if (!isFilled(value)) setConsent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phone = form.phone.trim();
    if (phone && !consent) {
      toast.error(t('consent.formRequired'));
      return;
    }
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...form,
        phone,
        consent: Boolean(phone && consent),
        captcha_token: captchaToken,
      });
      toast.success(t('contact.successMsg'));
      trackLead("contact");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (error) {
      toast.error(formatApiError(error, "Failed to send message. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = isDark
    ? "bg-black/50 border-white/10 text-white h-12 rounded-none placeholder:text-gray-600"
    : "bg-white border-gray-300 text-gray-900 h-12 rounded-none placeholder:text-gray-400";

  return (
    <section id="contact" className={`scroll-mt-24 py-24 md:py-32 transition-colors duration-300 ${isDark ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`} data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className={`text-sm uppercase tracking-widest font-medium mb-3 block ${isDark ? 'text-blue-500' : 'text-red-600'}`}>
              {t('contact.title')} {t('contact.titleAccent')}
            </span>
            <h2 className={`font-unbounded text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('nav.contact')}
            </h2>
            <p className={`mb-10 max-w-md ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('contact.description')}
            </p>

            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`p-5 border ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 flex items-center justify-center ${isDark ? 'bg-blue-600/10' : 'bg-red-600/10'}`}>
                      <item.icon className={`h-5 w-5 ${isDark ? 'text-blue-500' : 'text-red-600'}`} />
                    </div>
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</span>
                  </div>
                  {item.lines.map((line, i) => (
                    <p key={i} className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {line}
                    </p>
                  ))}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className={`p-8 border ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
              <h3 className={`font-unbounded text-xl font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('contact.formTitle')}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name" className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('contact.nameLabel')} *</Label>
                    <Input
                      id="contact-name"
                      value={form.name}
                      onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                      required
                      placeholder={t('contact.namePlaceholder')}
                      className={inputCls}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('contact.emailLabel')} *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      required
                      placeholder={t('contact.emailPlaceholder')}
                      className={inputCls}
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-phone" className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('contact.phoneLabel')} ({t('common.optional')})</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder={t('contact.phonePlaceholder')}
                    className={inputCls}
                    data-testid="contact-phone-input"
                  />
                </div>

                <PhoneConsentSlide open={phoneProvided}>
                  <ConsentAgreement
                    id="contact-consent"
                    checked={consent}
                    onCheckedChange={setConsent}
                    isDark={isDark}
                    testId="contact-consent-checkbox"
                    checkboxClassName={`mt-1 ${isDark ? '' : 'border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600'}`}
                  />
                </PhoneConsentSlide>

                <div className="space-y-2">
                  <Label htmlFor="contact-message" className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('contact.messageLabel')} *</Label>
                  <Textarea
                    id="contact-message"
                    value={form.message}
                    onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                    required
                    placeholder={t('contact.messagePlaceholder')}
                    className={`rounded-none min-h-[150px] resize-none ${isDark ? 'bg-black/50 border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'}`}
                    data-testid="contact-message-input"
                  />
                </div>

                <BotProtection onToken={setCaptchaToken} isDark={isDark} />

                <Button
                  type="submit"
                  disabled={submitting || !canSubmit}
                  className={`w-full rounded-none py-6 font-semibold uppercase tracking-wider disabled:opacity-50 ${
                    isDark 
                      ? 'bg-white text-black hover:bg-gray-200' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    t('contact.sending')
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {t('contact.sendBtn')}
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
